import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output, SimpleChanges,
  ViewChild
} from '@angular/core';
import {ModalDirective, ModalOptions} from 'ngx-bootstrap';
import {isNullOrUndefined} from 'util';
import {RcEventBusService} from '../../../rc-utils/rc-event-bus/rc-event-bus.service';

/**
 * Rc popup wrapper component - open a popup with received content once its loaded - currently uses ngx-bootstrap popup
 *
 * @example
 * <lib-rc-popup-wrapper *ngIf="openPopup" (closeCallback)="openPopup = false">
 *   <div>Some popup content</div>
 * </lib-rc-popup-wrapper>
 *
 * @example with template for close button
 * <lib-rc-popup-wrapper *ngIf="openPopup" (closeCallback)="openPopup = false">
 *   <div closeButton class="close-button"></div>
 *   <div>Some popup content</div>
 * </lib-rc-popup-wrapper>
 */
@Component({
  selector: 'lib-rc-popup-wrapper',
  template: `<ng-container *ngIf="renderContent()">
    <div role="dialog" class="rc-popup-wrapper modal fade" bsModal (onHidden)="onHide()" #modal="bs-modal">
        <div class="modal-dialog {{customClass}}">
            <div class="modal-content">
                <button #closeBtn attr.aria-label="{{ getCloseDialogButtonAriaLabel() }}" *ngIf="showCloseButton" type="button"
                        class="pointer close" data-dismiss="modal" (click)="close()">
                  <div #closeButtonContent><ng-content select="[closeButton]"></ng-content></div>
                    <span *ngIf="!closeButtonContent.innerHTML.trim()" aria-hidden="true">&times;</span>
                </button>
                <div class="modal-header" *ngIf="showHeaderSection">
                    <ng-content select="[popupHeader]"></ng-content>
                </div>

                <div class="modal-body">
                    <ng-content></ng-content>
                </div>
            </div>
        </div>
        <a href="javascript:void(0)" (keydown)="dialogEndBlur($event)" title="{{getEndOfDialogText()}}"></a>
    </div>
</ng-container>
`,
  styles: [`.rc-popup-wrapper .modal-dialog .modal-content{position:relative}.rc-popup-wrapper .modal-dialog .modal-content .pointer{cursor:pointer}.rc-popup-wrapper .modal-dialog .modal-content .close{z-index:1;position:absolute;right:10px;top:10px}`]
})
export class RcPopupWrapperComponent implements AfterViewInit, OnDestroy, OnChanges {

  @ViewChild(ModalDirective) modal: ModalDirective;
  @ViewChild('closeBtn') closeBtn: ElementRef;

  /**
   * Show / Hide close button
   */
  @Input() showCloseButton: boolean = true;

  /**
   * Accessibility - aria-label of close button
   */
  @Input() closeDialogButtonAriaLabel: string = 'close';

  /**
   * Accessibility - end of dialog aria-label
   */
  @Input() endOfDialogText: string;

  /**
   * display header section
   */
  @Input() showHeaderSection: true;

  /**
   * custom popup wrapper class
   */
  @Input() customClass: string = 'modal-md';

  /**
   * can close popup
   */
  @Input() canClose: boolean = true;

  /**
   * close callback
   */
  @Output() closeCallback: EventEmitter<any> = new EventEmitter();
  @Output() focusOutOfPopupEnd: EventEmitter<any> = new EventEmitter();

  public config: ModalOptions = {};
  public id: number;

  public _show = false;
  @Input() show: boolean = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (!isNullOrUndefined(this.show)) {
      if (changes.show.currentValue) {
        if (!this.canClose) {
          this.showCloseButton = false;
        }
        this._show = true;
        setTimeout(() => {
          this.openModal();
        }, 200);
      } else {
        if (changes.show.previousValue) {
          this.close();
          setTimeout(() => {
            this._show = false;
          }, 200);
        }
      }
    }

  }

  constructor(private eventBus: RcEventBusService) {
    this.id = new Date().getTime();
    this.eventBus.on(`CLOSE_ALL_POPUPS_${this.id}`, () => {
      this.close();
    }, true);
  }

  /**
   * Once component is instantiated, call openModal function
   */
  ngAfterViewInit(): void {
    if (isNullOrUndefined(this.show)) {
      if (!this.canClose) {
        this.showCloseButton = false;
      }
      setTimeout(() => {
        this.openModal();
      }, 200);
    }
  }

  ngOnDestroy() {
    this.eventBus.off(`CLOSE_ALL_POPUPS_${this.id}`, true);
    if (this.modal) {
      this.modal.hide();
    }
  }

  /**
   * attach on hide callback and emit if received output function
   */
  openModal(): void {
    if (!this.canClose) {
      this.config.backdrop = false;
      this.config.ignoreBackdropClick = true;

    }
    this.modal.config = this.config;
    this.modal.show();
    this.setFocus();
  }

  /**
   * set init focus to close button
   */
  setFocus(): void {
    if (this.closeBtn) {
      setTimeout(() => {
        const elem: any = this.closeBtn.nativeElement;
        elem.focus();
      }, 200);
    }
  }

  /**
   * close dialog
   */
  close(): void {
    if (this.modal) {
      this.modal.hide();
    }
  }

  /**
   * on popup hide
   */
  onHide(): void {
    if (this.closeCallback.observers.length) {
      this.closeCallback.emit();
    }
  }

  /**
   * on end of dialog blur
   * @param $event
   */
  dialogEndBlur($event): void {
    if ($event.keyCode === 9) {
      if (!$event.shiftKey) {
        if (this.closeBtn) {
          $event.preventDefault();
          const elem: any = this.closeBtn.nativeElement;
          elem.focus();
        } else {
          if (this.focusOutOfPopupEnd.observers.length) {
            this.focusOutOfPopupEnd.emit($event);
          }
        }
      }
    }
  }

  /**
   * get close dialog label text
   * @returns string
   */
  getCloseDialogButtonAriaLabel(): string {
    return this.closeDialogButtonAriaLabel ? this.closeDialogButtonAriaLabel : 'close dialog';
  }

  /**
   * get end of dialog label text
   * @returns string
   */
  getEndOfDialogText(): string {
    return this.endOfDialogText ? this.endOfDialogText : 'end of dialog';
  }

  renderContent() {
    return isNullOrUndefined(this.show) || this._show;
  }
}
